import {
  type CrmAgencyConnectionProviderResponse,
  type CrmAgencyConnectionProviderResponseProvider,
  CrmAgencyConnectionProviderResponseStatus,
} from '@urbanx/agx.crm.client';
import { AgxSpacer, AgxTypography, AgxBadge, AgxButton, Spinner } from '@urbanx/agx.ui-components2';
import CrmIcon from './CrmIcon';
import './CrmCard.scss';

interface CrmCardProps {
  activeProvider?: CrmAgencyConnectionProviderResponseProvider;
  busyProvider: CrmAgencyConnectionProviderResponseProvider | null;
  providerDetails: CrmAgencyConnectionProviderResponse;
  requestPending: boolean;
  onConnect: (providerDetails: CrmAgencyConnectionProviderResponse) => void;
  onDisconnect: (provider: CrmAgencyConnectionProviderResponseProvider) => void;
}

const CrmCard = ({
  activeProvider,
  busyProvider,
  providerDetails,
  requestPending,
  onConnect,
  onDisconnect,
}: CrmCardProps) => {
  if (!providerDetails.provider) return null;

  const crmProvider = providerDetails.provider;
  const connectionStatus =
    providerDetails.status ?? CrmAgencyConnectionProviderResponseStatus.NotConnected;
  const providerName = providerDetails.displayName || crmProvider;
  const connected = connectionStatus === CrmAgencyConnectionProviderResponseStatus.Connected;
  const connectable = providerDetails.connectable === true;
  const disabledByActiveProvider = Boolean(activeProvider) && activeProvider !== crmProvider;
  const busy = busyProvider === crmProvider;
  const disabled =
    disabledByActiveProvider || (!connected && !connectable) || (requestPending && !busy);

  const actionText = connected ? 'Disconnect' : 'Connect';

  return (
    <AgxSpacer
      direction="vertical"
      padding={{
        top: 'sm',
        bottom: 'sm',
        right: 'sm',
        left: 'sm',
      }}
      gap="sm"
      style={{
        borderRadius: 'var(--corner-sm, 16px)',
        background: 'var(--bg-secondary, #FFF)',
        boxShadow: '0 1px 4px 1px rgba(0, 0, 0, 0.08), 0 2px 3px 1px rgba(0, 0, 0, 0.04)',
      }}
      fill
      contentJustification="space-between"
    >
      <AgxSpacer direction="vertical" gap="sm" fill>
        <AgxBadge
          type="colourfill"
          size="sm"
          colour={connected ? 'green' : 'grey'}
          text={connected ? 'Connected' : 'Not connected'}
        />
        <AgxSpacer direction="horizontal" gap="spacing-lg" contentAlignment="center">
          <CrmIcon crmProvider={crmProvider} />
          <AgxTypography
            id={`${crmProvider}-name`}
            type={'body_ui_strong'}
            size={'medium'}
            color="var(--text-primary)"
          >
            {providerName}
          </AgxTypography>
        </AgxSpacer>

        {!disabledByActiveProvider && !connectable && !connected && (
          <AgxTypography id={`${crmProvider}-coming-soon-label`} type="label" size="small">
            Coming Soon
          </AgxTypography>
        )}
      </AgxSpacer>

      <AgxButton
        id={`${crmProvider}-${connected ? 'disconnect' : 'connect'}`}
        size="small"
        variant={connected ? 'hollow' : 'primary'}
        leftIcon={busy ? <Spinner className="crmCard__buttonSpinner" size={16} /> : undefined}
        text={busy ? (connected ? 'Disconnecting' : 'Connecting') : actionText}
        disabled={disabled || busy}
        onClick={connected ? () => onDisconnect(crmProvider) : () => onConnect(providerDetails)}
        stretch
      />
    </AgxSpacer>
  );
};

export default CrmCard;
